import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useToast } from '../components/ui/use-toast';
import {
  Brain,
  Send,
  AlertTriangle,
  Target,
  BarChart3,
  Globe,
  Building2,
  Users,
  Zap,
  MessageSquare,
  Lightbulb,
  ChevronRight,
  RefreshCw,
  Bot,
  Sparkles,
  TrendingUp,
} from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface InsightCard {
  id: string;
  title: string;
  description: string;
  type: 'opportunity' | 'risk' | 'trend' | 'action';
  confidence: number;
  impact: 'high' | 'medium' | 'low';
}

const mockInsights: InsightCard[] = [
  {
    id: '1',
    title: 'Miami Real Estate Surge Detected',
    description:
      'Property values in Miami-Dade have increased 12% this quarter. Consider accelerating tokenization of Florida assets.',
    type: 'opportunity',
    confidence: 87,
    impact: 'high',
  },
  {
    id: '2',
    title: 'Regulatory Update: SEC Guidelines',
    description:
      'New SEC guidelines on tokenized securities may affect Reg D offerings. Review compliance for 3 pending assets.',
    type: 'risk',
    confidence: 94,
    impact: 'high',
  },
  {
    id: '3',
    title: 'Investor Demand: Commercial Retail',
    description:
      'Increased investor interest in commercial retail properties. 23% more inquiries this month.',
    type: 'trend',
    confidence: 78,
    impact: 'medium',
  },
  {
    id: '4',
    title: 'Yield Optimization Opportunity',
    description:
      'Sunset Apartments rental income can be increased by 8% based on market comparables.',
    type: 'action',
    confidence: 82,
    impact: 'medium',
  },
];

const quickActions = [
  { label: 'Analyze portfolio risk', icon: AlertTriangle },
  { label: 'Predict yield trends', icon: TrendingUp },
  { label: 'Optimize asset allocation', icon: Target },
  { label: 'Generate investor report', icon: BarChart3 },
];

const marketMetrics = [
  { label: 'Market Sentiment', value: 'Bullish', change: '+12%', positive: true },
  { label: 'Tokenization Volume', value: '$2.4B', change: '+8.3%', positive: true },
  { label: 'Avg. Property Yield', value: '6.2%', change: '-0.3%', positive: false },
  { label: 'Investor Activity', value: 'High', change: '+24%', positive: true },
];

export default function VestnInsightAIPage() {
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content:
        "Hello! I'm VESTN Insight AI, your intelligent assistant for real estate tokenization and portfolio management. I can help you analyze market trends, identify investment opportunities, assess risks, and optimize your portfolio. What would you like to explore today?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');

  const handleSend = async (optionalMessage?: string) => {
    const textToSend = (optionalMessage ?? input).trim();
    if (!textToSend) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: textToSend,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    if (!optionalMessage) setInput('');
    setIsLoading(true);

    try {
      const allMessages = [...messages, userMessage].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const supabaseUrl =
        (typeof process !== 'undefined' && process.env.REACT_APP_SUPABASE_URL) ||
        (typeof import.meta !== 'undefined' &&
          (import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL) ||
        '';
      const supabaseKey =
        (typeof process !== 'undefined' && process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY) ||
        (typeof import.meta !== 'undefined' &&
          (import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_PUBLISHABLE_KEY) ||
        '';

      const response = await fetch(`${supabaseUrl}/functions/v1/vestn-ai-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({ messages: allMessages }),
      });

      if (!response.ok) {
        throw new Error('AI request failed');
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader');

      const decoder = new TextDecoder();
      let assistantContent = '';

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          streamDone = true;
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ') && line !== 'data: [DONE]') {
            try {
              const json = JSON.parse(line.slice(6));
              const content = json.choices?.[0]?.delta?.content;
              if (content) {
                assistantContent += content;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessage.id ? { ...m, content: assistantContent } : m
                  )
                );
              }
            } catch {
              // ignore parse errors for streaming
            }
          }
        }
      }
    } catch (error) {
      toast({
        title: 'AI Error',
        description: 'Failed to get AI response. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action: string) => {
    handleSend(action);
  };

  const getInsightIcon = (type: InsightCard['type']) => {
    switch (type) {
      case 'opportunity':
        return <Sparkles className='w-4 h-4 text-primary' />;
      case 'risk':
        return <AlertTriangle className='w-4 h-4 text-destructive' />;
      case 'trend':
        return <TrendingUp className='w-4 h-4 text-info' />;
      case 'action':
        return <Target className='w-4 h-4 text-success' />;
    }
  };

  const getImpactColor = (impact: InsightCard['impact']) => {
    switch (impact) {
      case 'high':
        return 'bg-destructive/20 text-destructive';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-success/20 text-success';
    }
  };

  return (
    <div className='space-y-6 animate-fade-in'>
      {/* Header */}
      <div className='flex flex-col lg:flex-row lg:items-center justify-between gap-4'>
        <div className='flex items-center gap-4'>
          <div className='relative'>
            <div className='w-14 h-14 rounded-2xl bg-gradient-to-br from-primary via-primary/80 to-secondary flex items-center justify-center glow-primary'>
              <Brain className='w-7 h-7 text-primary-foreground' />
            </div>
            <div className='absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-success flex items-center justify-center'>
              <Zap className='w-3 h-3 text-success-foreground' />
            </div>
          </div>
          <div>
            <h1 className='text-2xl font-bold tracking-tight'>VESTN Insight AI</h1>
            <p className='text-muted-foreground'>
              Intelligent analytics & decision support for your RWA portfolio
            </p>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <Badge variant='outline' className='gap-1.5 py-1.5 border-success/50 text-success'>
            <span className='w-2 h-2 rounded-full bg-success animate-pulse' />
            AI Online
          </Badge>
          <Button
            variant='outline'
            size='sm'
            onClick={() => toast({ title: 'Syncing latest data...' })}
          >
            <RefreshCw className='w-4 h-4 mr-2' />
            Sync Data
          </Button>
        </div>
      </div>

      {/* Market Metrics Strip */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
        {marketMetrics.map((metric, i) => (
          <Card
            key={i}
            className='gradient-card border-border/30 hover:border-primary/30 transition-all'
          >
            <CardContent className='p-4'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-muted-foreground'>{metric.label}</span>
                <span
                  className={`text-xs font-medium ${metric.positive ? 'text-success' : 'text-destructive'}`}
                >
                  {metric.change}
                </span>
              </div>
              <p className='text-xl font-bold mt-1'>{metric.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className='space-y-4'>
        <TabsList className='bg-muted/50 border border-border/50'>
          <TabsTrigger value='chat' className='gap-2 data-[state=active]:bg-card'>
            <MessageSquare className='w-4 h-4' />
            AI Chat
          </TabsTrigger>
          <TabsTrigger value='insights' className='gap-2 data-[state=active]:bg-card'>
            <Lightbulb className='w-4 h-4' />
            Smart Insights
          </TabsTrigger>
          <TabsTrigger value='analytics' className='gap-2 data-[state=active]:bg-card'>
            <BarChart3 className='w-4 h-4' />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Chat Tab */}
        <TabsContent value='chat' className='space-y-4'>
          <div className='grid lg:grid-cols-4 gap-6'>
            {/* Chat Area */}
            <Card className='lg:col-span-3 gradient-card border-border/30'>
              <CardContent className='p-0 flex flex-col h-[600px]'>
                {/* Messages */}
                <ScrollArea className='flex-1 p-6'>
                  <div className='space-y-6'>
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex gap-4 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                      >
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                            message.role === 'assistant'
                              ? 'bg-gradient-to-br from-primary to-secondary'
                              : 'bg-muted'
                          }`}
                        >
                          {message.role === 'assistant' ? (
                            <Bot className='w-5 h-5 text-primary-foreground' />
                          ) : (
                            <Users className='w-5 h-5 text-muted-foreground' />
                          )}
                        </div>
                        <div className={`flex-1 ${message.role === 'user' ? 'text-right' : ''}`}>
                          <div
                            className={`inline-block p-4 rounded-2xl max-w-[90%] ${
                              message.role === 'assistant'
                                ? 'ai-message bg-muted/50 text-left'
                                : 'bg-primary/20 border border-primary/30'
                            }`}
                          >
                            <p className='text-sm leading-relaxed whitespace-pre-line'>
                              {message.content}
                            </p>
                          </div>
                          <p className='text-xs text-muted-foreground mt-2'>
                            {message.timestamp.toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                    {isLoading && (
                      <div className='flex gap-4'>
                        <div className='w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center'>
                          <Bot className='w-5 h-5 text-primary-foreground animate-pulse' />
                        </div>
                        <div className='flex items-center gap-2 text-muted-foreground'>
                          <div className='flex gap-1'>
                            <span
                              className='w-2 h-2 rounded-full bg-primary animate-bounce'
                              style={{ animationDelay: '0ms' }}
                            />
                            <span
                              className='w-2 h-2 rounded-full bg-primary animate-bounce'
                              style={{ animationDelay: '150ms' }}
                            />
                            <span
                              className='w-2 h-2 rounded-full bg-primary animate-bounce'
                              style={{ animationDelay: '300ms' }}
                            />
                          </div>
                          <span className='text-sm'>Analyzing...</span>
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className='p-4 border-t border-border/50'>
                  <div className='flex gap-3'>
                    <Input
                      placeholder='Ask about market trends, portfolio analysis, or investment opportunities...'
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      className='bg-muted/50 border-border/50 focus:border-primary/50'
                    />
                    <Button
                      onClick={() => handleSend()}
                      disabled={isLoading || !input.trim()}
                      className='glow-primary'
                    >
                      <Send className='w-4 h-4' />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions Sidebar */}
            <div className='space-y-4'>
              <Card className='gradient-card border-border/30'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-medium flex items-center gap-2'>
                    <Zap className='w-4 h-4 text-primary' />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-2'>
                  {quickActions.map((action, i) => (
                    <Button
                      key={i}
                      variant='ghost'
                      size='sm'
                      className='w-full justify-start gap-2 text-left h-auto py-3 hover:bg-primary/10'
                      onClick={() => handleQuickAction(action.label)}
                    >
                      <action.icon className='w-4 h-4 text-muted-foreground' />
                      <span className='flex-1'>{action.label}</span>
                      <ChevronRight className='w-4 h-4 text-muted-foreground' />
                    </Button>
                  ))}
                </CardContent>
              </Card>

              <Card className='gradient-card border-border/30'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-medium flex items-center gap-2'>
                    <Globe className='w-4 h-4 text-secondary' />
                    Data Sources
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-3'>
                  {[
                    { name: 'Market Data', status: 'Live' },
                    { name: 'Portfolio Feed', status: 'Live' },
                    { name: 'News & Events', status: 'Live' },
                    { name: 'Regulatory Updates', status: '15m ago' },
                  ].map((source, i) => (
                    <div key={i} className='flex items-center justify-between text-sm'>
                      <span className='text-muted-foreground'>{source.name}</span>
                      <Badge
                        variant='outline'
                        className={source.status === 'Live' ? 'border-success/50 text-success' : ''}
                      >
                        {source.status}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value='insights' className='space-y-4'>
          <div className='grid md:grid-cols-2 gap-4'>
            {mockInsights.map((insight) => (
              <Card
                key={insight.id}
                className='gradient-card border-border/30 hover:border-primary/30 transition-all cursor-pointer group'
              >
                <CardContent className='p-5'>
                  <div className='flex items-start gap-4'>
                    <div className='w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center shrink-0'>
                      {getInsightIcon(insight.type)}
                    </div>
                    <div className='flex-1 space-y-3'>
                      <div className='flex items-start justify-between gap-2'>
                        <h3 className='font-semibold group-hover:text-primary transition-colors'>
                          {insight.title}
                        </h3>
                        <Badge className={getImpactColor(insight.impact)} variant='secondary'>
                          {insight.impact}
                        </Badge>
                      </div>
                      <p className='text-sm text-muted-foreground leading-relaxed'>
                        {insight.description}
                      </p>
                      <div className='flex items-center gap-3'>
                        <div className='flex-1'>
                          <div className='flex items-center justify-between text-xs mb-1'>
                            <span className='text-muted-foreground'>Confidence</span>
                            <span className='font-medium'>{insight.confidence}%</span>
                          </div>
                          <Progress value={insight.confidence} className='h-1.5' />
                        </div>
                        <Button size='sm' variant='ghost' className='text-primary'>
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value='analytics' className='space-y-4'>
          <div className='grid md:grid-cols-3 gap-4'>
            {/* Portfolio Health */}
            <Card className='gradient-card border-border/30'>
              <CardHeader>
                <CardTitle className='text-sm font-medium flex items-center gap-2'>
                  <Building2 className='w-4 h-4 text-primary' />
                  Portfolio Health Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='flex items-center justify-center py-4'>
                  <div className='relative w-32 h-32'>
                    <svg className='w-full h-full transform -rotate-90'>
                      <circle
                        cx='64'
                        cy='64'
                        r='56'
                        stroke='currentColor'
                        strokeWidth='12'
                        fill='none'
                        className='text-muted/30'
                      />
                      <circle
                        cx='64'
                        cy='64'
                        r='56'
                        stroke='url(#gradient-vestn)'
                        strokeWidth='12'
                        fill='none'
                        strokeDasharray={`${352 * 0.85} 352`}
                        strokeLinecap='round'
                      />
                      <defs>
                        <linearGradient id='gradient-vestn' x1='0%' y1='0%' x2='100%' y2='0%'>
                          <stop offset='0%' stopColor='hsl(72 77% 44%)' />
                          <stop offset='100%' stopColor='hsl(170 100% 16%)' />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className='absolute inset-0 flex flex-col items-center justify-center'>
                      <span className='text-3xl font-bold'>85</span>
                      <span className='text-xs text-muted-foreground'>Excellent</span>
                    </div>
                  </div>
                </div>
                <div className='space-y-2 mt-4'>
                  {[
                    { label: 'Diversification', value: 90 },
                    { label: 'Liquidity', value: 75 },
                    { label: 'Yield Performance', value: 88 },
                  ].map((item, i) => (
                    <div key={i} className='space-y-1'>
                      <div className='flex justify-between text-xs'>
                        <span className='text-muted-foreground'>{item.label}</span>
                        <span>{item.value}%</span>
                      </div>
                      <Progress value={item.value} className='h-1' />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* AI Recommendations */}
            <Card className='gradient-card border-border/30'>
              <CardHeader>
                <CardTitle className='text-sm font-medium flex items-center gap-2'>
                  <Sparkles className='w-4 h-4 text-primary' />
                  AI Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                {[
                  {
                    action: 'Increase allocation to Austin market',
                    priority: 'High',
                  },
                  {
                    action: 'Review rent pricing for Miami assets',
                    priority: 'Medium',
                  },
                  {
                    action: 'Complete compliance check for 2 assets',
                    priority: 'High',
                  },
                  {
                    action: 'Consider yield reinvestment strategy',
                    priority: 'Low',
                  },
                ].map((rec, i) => (
                  <div
                    key={i}
                    className='flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors'
                  >
                    <div
                      className={`w-2 h-2 rounded-full mt-1.5 ${
                        rec.priority === 'High'
                          ? 'bg-destructive'
                          : rec.priority === 'Medium'
                            ? 'bg-warning'
                            : 'bg-success'
                      }`}
                    />
                    <div className='flex-1'>
                      <p className='text-sm'>{rec.action}</p>
                      <span className='text-xs text-muted-foreground'>{rec.priority} Priority</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Trend Predictions */}
            <Card className='gradient-card border-border/30'>
              <CardHeader>
                <CardTitle className='text-sm font-medium flex items-center gap-2'>
                  <TrendingUp className='w-4 h-4 text-primary' />
                  Trend Predictions
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                {[
                  {
                    market: 'Miami',
                    trend: '↑ 8.2%',
                    period: 'Next 6 months',
                  },
                  {
                    market: 'Austin',
                    trend: '↑ 12.5%',
                    period: 'Next 6 months',
                  },
                  {
                    market: 'Nashville',
                    trend: '↑ 9.1%',
                    period: 'Next 6 months',
                  },
                  {
                    market: 'NYC',
                    trend: '↓ 2.3%',
                    period: 'Next 6 months',
                  },
                ].map((pred, i) => (
                  <div
                    key={i}
                    className='flex items-center justify-between p-3 rounded-lg bg-muted/30'
                  >
                    <div>
                      <p className='font-medium'>{pred.market}</p>
                      <span className='text-xs text-muted-foreground'>{pred.period}</span>
                    </div>
                    <span
                      className={`font-mono font-bold ${
                        pred.trend.startsWith('↑') ? 'text-success' : 'text-destructive'
                      }`}
                    >
                      {pred.trend}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
